/**
 * Personal Finance Panel (SPRINT-28 F022)
 *
 * CRUD panel for managing personal financial statements on a contact.
 * Shows statement cards with net worth summary, and a form with
 * accordion sections for assets, liabilities, income, and debt servicing.
 * All totals are auto-calculated client-side on every field change.
 *
 * States:
 * - Empty: No statements, shows "No personal financial statements" with Add button
 * - List: Card per statement with date, label, net worth
 * - Form: Accordion-based form with auto-calculated totals
 * - Delete: AlertDialog confirmation
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Loader2, DollarSign, Edit } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AccountOption {
  id: string;
  legalName: string;
}

interface PersonalFinancialStatement {
  id: string;
  contactId: string;
  accountId: string;
  statementDate: string;
  statementLabel: string | null;
  realEstate: number;
  vehicles: number;
  sharesInvestments: number;
  superannuation: number;
  bankAccounts: number;
  otherAssets: number;
  totalAssets: number;
  mortgages: number;
  personalLoans: number;
  creditCards: number;
  otherLiabilities: number;
  totalLiabilities: number;
  netWorth: number;
  salaryIncome: number;
  rentalIncome: number;
  dividendIncome: number;
  otherIncome: number;
  totalIncome: number;
  mortgageRepayments: number;
  personalLoanRepayments: number;
  creditCardMinimum: number;
  otherCommitments: number;
  totalPersonalDebtService: number;
  includeInServiceability: number;
  serviceabilityContributionType: string | null;
  sourceType: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
}

interface FormState {
  statementDate: string;
  statementLabel: string;
  accountId: string;
  // Assets
  realEstate: string;
  vehicles: string;
  sharesInvestments: string;
  superannuation: string;
  bankAccounts: string;
  otherAssets: string;
  // Liabilities
  mortgages: string;
  personalLoans: string;
  creditCards: string;
  otherLiabilities: string;
  // Income
  salaryIncome: string;
  rentalIncome: string;
  dividendIncome: string;
  otherIncome: string;
  // Debt Servicing
  mortgageRepayments: string;
  personalLoanRepayments: string;
  creditCardMinimum: string;
  otherCommitments: string;
  // Serviceability
  includeInServiceability: boolean;
  serviceabilityContributionType: string;
  // Notes
  notes: string;
}

export interface PersonalFinancePanelProps {
  contactId: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SERVICEABILITY_TYPES = [
  { value: 'full', label: 'Full' },
  { value: 'partial', label: 'Partial' },
  { value: 'excluded', label: 'Excluded' },
];

const INITIAL_FORM_STATE: FormState = {
  statementDate: new Date().toISOString().split('T')[0],
  statementLabel: '',
  accountId: '',
  realEstate: '',
  vehicles: '',
  sharesInvestments: '',
  superannuation: '',
  bankAccounts: '',
  otherAssets: '',
  mortgages: '',
  personalLoans: '',
  creditCards: '',
  otherLiabilities: '',
  salaryIncome: '',
  rentalIncome: '',
  dividendIncome: '',
  otherIncome: '',
  mortgageRepayments: '',
  personalLoanRepayments: '',
  creditCardMinimum: '',
  otherCommitments: '',
  includeInServiceability: false,
  serviceabilityContributionType: 'full',
  notes: '',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseNum(val: string): number {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
}

function formatCurrency(amount: number): string {
  const isNegative = amount < 0;
  const formatted = Math.abs(amount).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return isNegative ? `-$${formatted}` : `$${formatted}`;
}

function formatCurrencyShort(amount: number): string {
  const isNegative = amount < 0;
  const absAmount = Math.abs(amount);
  const formatted = absAmount.toLocaleString('en-AU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return isNegative ? `-$${formatted}` : `$${formatted}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PersonalFinancePanel({ contactId }: PersonalFinancePanelProps) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingStatement, setEditingStatement] = useState<PersonalFinancialStatement | null>(null);
  const [deleteItem, setDeleteItem] = useState<PersonalFinancialStatement | null>(null);
  const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const queryKey = `/api/financial/contacts/${contactId}/personal-statements`;

  // Fetch statements
  const { data: statements = [], isLoading } = useQuery<PersonalFinancialStatement[]>({
    queryKey: [queryKey],
    enabled: !!contactId,
  });

  // Fetch accounts for dropdown
  const { data: accounts = [] } = useQuery<AccountOption[]>({
    queryKey: ['/api/crm/accounts'],
    enabled: !!contactId,
  });

  // -----------------------------------------------------------------------
  // Auto-calculated totals
  // -----------------------------------------------------------------------

  const totalAssets = useMemo(() => {
    return (
      parseNum(formState.realEstate) +
      parseNum(formState.vehicles) +
      parseNum(formState.sharesInvestments) +
      parseNum(formState.superannuation) +
      parseNum(formState.bankAccounts) +
      parseNum(formState.otherAssets)
    );
  }, [
    formState.realEstate,
    formState.vehicles,
    formState.sharesInvestments,
    formState.superannuation,
    formState.bankAccounts,
    formState.otherAssets,
  ]);

  const totalLiabilities = useMemo(() => {
    return (
      parseNum(formState.mortgages) +
      parseNum(formState.personalLoans) +
      parseNum(formState.creditCards) +
      parseNum(formState.otherLiabilities)
    );
  }, [
    formState.mortgages,
    formState.personalLoans,
    formState.creditCards,
    formState.otherLiabilities,
  ]);

  const netWorth = useMemo(() => totalAssets - totalLiabilities, [totalAssets, totalLiabilities]);

  const totalIncome = useMemo(() => {
    return (
      parseNum(formState.salaryIncome) +
      parseNum(formState.rentalIncome) +
      parseNum(formState.dividendIncome) +
      parseNum(formState.otherIncome)
    );
  }, [
    formState.salaryIncome,
    formState.rentalIncome,
    formState.dividendIncome,
    formState.otherIncome,
  ]);

  const totalDebtService = useMemo(() => {
    return (
      parseNum(formState.mortgageRepayments) +
      parseNum(formState.personalLoanRepayments) +
      parseNum(formState.creditCardMinimum) +
      parseNum(formState.otherCommitments)
    );
  }, [
    formState.mortgageRepayments,
    formState.personalLoanRepayments,
    formState.creditCardMinimum,
    formState.otherCommitments,
  ]);

  // -----------------------------------------------------------------------
  // Mutations
  // -----------------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiRequest('POST', queryKey, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast({ title: 'Statement created', description: 'Personal financial statement saved successfully' });
      handleCloseForm();
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const res = await apiRequest('PATCH', `${queryKey}/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast({ title: 'Statement updated', description: 'Personal financial statement updated successfully' });
      handleCloseForm();
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `${queryKey}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast({ title: 'Statement deleted', description: 'Personal financial statement removed' });
      setDeleteItem(null);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleOpenAdd = useCallback(() => {
    setEditingStatement(null);
    setFormState(INITIAL_FORM_STATE);
    setIsFormOpen(true);
  }, []);

  const handleOpenEdit = useCallback((stmt: PersonalFinancialStatement) => {
    setEditingStatement(stmt);
    setFormState({
      statementDate: stmt.statementDate || new Date().toISOString().split('T')[0],
      statementLabel: stmt.statementLabel || '',
      accountId: stmt.accountId || '',
      realEstate: String(stmt.realEstate || ''),
      vehicles: String(stmt.vehicles || ''),
      sharesInvestments: String(stmt.sharesInvestments || ''),
      superannuation: String(stmt.superannuation || ''),
      bankAccounts: String(stmt.bankAccounts || ''),
      otherAssets: String(stmt.otherAssets || ''),
      mortgages: String(stmt.mortgages || ''),
      personalLoans: String(stmt.personalLoans || ''),
      creditCards: String(stmt.creditCards || ''),
      otherLiabilities: String(stmt.otherLiabilities || ''),
      salaryIncome: String(stmt.salaryIncome || ''),
      rentalIncome: String(stmt.rentalIncome || ''),
      dividendIncome: String(stmt.dividendIncome || ''),
      otherIncome: String(stmt.otherIncome || ''),
      mortgageRepayments: String(stmt.mortgageRepayments || ''),
      personalLoanRepayments: String(stmt.personalLoanRepayments || ''),
      creditCardMinimum: String(stmt.creditCardMinimum || ''),
      otherCommitments: String(stmt.otherCommitments || ''),
      includeInServiceability: stmt.includeInServiceability === 1,
      serviceabilityContributionType: stmt.serviceabilityContributionType || 'full',
      notes: stmt.notes || '',
    });
    setIsFormOpen(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingStatement(null);
    setFormState(INITIAL_FORM_STATE);
  }, []);

  const handleFieldChange = useCallback((field: keyof FormState, value: string | boolean) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = useCallback(() => {
    const payload: Record<string, unknown> = {
      statementDate: formState.statementDate,
      statementLabel: formState.statementLabel || null,
      accountId: formState.accountId || undefined,
      realEstate: formState.realEstate || '0',
      vehicles: formState.vehicles || '0',
      sharesInvestments: formState.sharesInvestments || '0',
      superannuation: formState.superannuation || '0',
      bankAccounts: formState.bankAccounts || '0',
      otherAssets: formState.otherAssets || '0',
      mortgages: formState.mortgages || '0',
      personalLoans: formState.personalLoans || '0',
      creditCards: formState.creditCards || '0',
      otherLiabilities: formState.otherLiabilities || '0',
      salaryIncome: formState.salaryIncome || '0',
      rentalIncome: formState.rentalIncome || '0',
      dividendIncome: formState.dividendIncome || '0',
      otherIncome: formState.otherIncome || '0',
      mortgageRepayments: formState.mortgageRepayments || '0',
      personalLoanRepayments: formState.personalLoanRepayments || '0',
      creditCardMinimum: formState.creditCardMinimum || '0',
      otherCommitments: formState.otherCommitments || '0',
      includeInServiceability: formState.includeInServiceability ? 1 : 0,
      serviceabilityContributionType: formState.serviceabilityContributionType || null,
      notes: formState.notes || null,
      sourceType: 'manual',
    };

    if (editingStatement) {
      updateMutation.mutate({ id: editingStatement.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }, [formState, editingStatement, createMutation, updateMutation]);

  // -----------------------------------------------------------------------
  // Currency input helper
  // -----------------------------------------------------------------------

  const CurrencyField = ({
    fieldName,
    label,
  }: {
    fieldName: keyof FormState;
    label: string;
  }) => (
    <div className="space-y-1">
      <Label htmlFor={`field-${fieldName}`} className="text-xs">
        {label}
      </Label>
      <div className="relative">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
          $
        </span>
        <Input
          id={`field-${fieldName}`}
          data-testid={`field-${fieldName}`}
          type="number"
          step="0.01"
          min="0"
          className="pl-6"
          value={formState[fieldName] as string}
          onChange={(e) => handleFieldChange(fieldName, e.target.value)}
          placeholder="0.00"
        />
      </div>
    </div>
  );

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <Card data-testid="personal-statement-list">
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          Personal Financial Statements
        </CardTitle>
        <Button
          size="sm"
          data-testid="personal-statement-add-button"
          onClick={handleOpenAdd}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Statement
        </Button>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : statements.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No personal financial statements. Click &apos;Add Statement&apos; to begin.
          </div>
        ) : (
          <div className="space-y-3">
            {statements.map((stmt) => (
              <Card key={stmt.id} className="border">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-sm">
                        {stmt.statementLabel || 'Untitled Statement'}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {stmt.statementDate
                          ? new Date(stmt.statementDate + 'T00:00:00').toLocaleDateString('en-AU', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })
                          : 'No date'}
                      </div>
                      <div className="mt-2 flex gap-4 text-xs">
                        <div>
                          <span className="text-muted-foreground">Assets: </span>
                          <span className="font-medium">
                            {formatCurrencyShort(stmt.totalAssets || 0)}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Liabilities: </span>
                          <span className="font-medium">
                            {formatCurrencyShort(stmt.totalLiabilities || 0)}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Net Worth: </span>
                          <span
                            className={`font-semibold ${
                              (stmt.netWorth || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {formatCurrencyShort(stmt.netWorth || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEdit(stmt)}
                        data-testid={`personal-statement-edit-button-${stmt.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => setDeleteItem(stmt)}
                        data-testid={`personal-statement-delete-button-${stmt.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={(open) => !open && handleCloseForm()}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="personal-statement-form"
        >
          <DialogHeader>
            <DialogTitle>
              {editingStatement ? 'Edit' : 'Add'} Personal Financial Statement
            </DialogTitle>
            <DialogDescription>
              Enter the financial position for this contact. Totals are calculated automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Header Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="field-statementDate" className="text-xs">
                  Statement Date
                </Label>
                <Input
                  id="field-statementDate"
                  data-testid="field-statementDate"
                  type="date"
                  value={formState.statementDate}
                  onChange={(e) => handleFieldChange('statementDate', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="field-statementLabel" className="text-xs">
                  Statement Label
                </Label>
                <Input
                  id="field-statementLabel"
                  data-testid="field-statementLabel"
                  value={formState.statementLabel}
                  onChange={(e) => handleFieldChange('statementLabel', e.target.value)}
                  placeholder="e.g., Q1 2026 Statement"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Account Context</Label>
              <Select
                value={formState.accountId}
                onValueChange={(val) => handleFieldChange('accountId', val)}
              >
                <SelectTrigger data-testid="field-accountId">
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.legalName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Accordion Sections */}
            <Accordion type="multiple" defaultValue={['assets', 'liabilities', 'net-position', 'income', 'debt-servicing']}>
              {/* Assets Section */}
              <AccordionItem value="assets">
                <AccordionTrigger className="text-sm font-semibold">
                  Assets
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-2 gap-3">
                    <CurrencyField fieldName="realEstate" label="Real Estate" />
                    <CurrencyField fieldName="vehicles" label="Vehicles" />
                    <CurrencyField fieldName="sharesInvestments" label="Shares & Investments" />
                    <CurrencyField fieldName="superannuation" label="Superannuation" />
                    <CurrencyField fieldName="bankAccounts" label="Bank Accounts" />
                    <CurrencyField fieldName="otherAssets" label="Other Assets" />
                  </div>
                  <div
                    className="mt-3 pt-3 border-t flex justify-between items-center"
                    data-testid="total-assets"
                  >
                    <span className="text-sm font-semibold">Total Assets</span>
                    <span className="text-sm font-bold">
                      {formatCurrency(totalAssets)}
                    </span>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Liabilities Section */}
              <AccordionItem value="liabilities">
                <AccordionTrigger className="text-sm font-semibold">
                  Liabilities
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-2 gap-3">
                    <CurrencyField fieldName="mortgages" label="Mortgages" />
                    <CurrencyField fieldName="personalLoans" label="Personal Loans" />
                    <CurrencyField fieldName="creditCards" label="Credit Cards" />
                    <CurrencyField fieldName="otherLiabilities" label="Other Liabilities" />
                  </div>
                  <div
                    className="mt-3 pt-3 border-t flex justify-between items-center"
                    data-testid="total-liabilities"
                  >
                    <span className="text-sm font-semibold">Total Liabilities</span>
                    <span className="text-sm font-bold">
                      {formatCurrency(totalLiabilities)}
                    </span>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Net Position Section */}
              <AccordionItem value="net-position">
                <AccordionTrigger className="text-sm font-semibold">
                  Net Position
                </AccordionTrigger>
                <AccordionContent>
                  <div
                    className="flex justify-between items-center p-3 rounded-md bg-muted"
                    data-testid="net-worth"
                  >
                    <span className="text-sm font-semibold">Net Worth</span>
                    <span
                      className={`text-lg font-bold ${
                        netWorth >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {formatCurrency(netWorth)}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    Net Worth = Total Assets ({formatCurrency(totalAssets)}) - Total Liabilities ({formatCurrency(totalLiabilities)})
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Income Section */}
              <AccordionItem value="income">
                <AccordionTrigger className="text-sm font-semibold">
                  Income
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-2 gap-3">
                    <CurrencyField fieldName="salaryIncome" label="Salary Income" />
                    <CurrencyField fieldName="rentalIncome" label="Rental Income" />
                    <CurrencyField fieldName="dividendIncome" label="Dividend Income" />
                    <CurrencyField fieldName="otherIncome" label="Other Income" />
                  </div>
                  <div
                    className="mt-3 pt-3 border-t flex justify-between items-center"
                    data-testid="total-income"
                  >
                    <span className="text-sm font-semibold">Total Income</span>
                    <span className="text-sm font-bold">
                      {formatCurrency(totalIncome)}
                    </span>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Debt Servicing Section */}
              <AccordionItem value="debt-servicing">
                <AccordionTrigger className="text-sm font-semibold">
                  Debt Servicing
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-2 gap-3">
                    <CurrencyField fieldName="mortgageRepayments" label="Mortgage Repayments" />
                    <CurrencyField fieldName="personalLoanRepayments" label="Personal Loan Repayments" />
                    <CurrencyField fieldName="creditCardMinimum" label="Credit Card Minimum" />
                    <CurrencyField fieldName="otherCommitments" label="Other Commitments" />
                  </div>
                  <div
                    className="mt-3 pt-3 border-t flex justify-between items-center"
                    data-testid="total-debt-service"
                  >
                    <span className="text-sm font-semibold">Total Debt Service</span>
                    <span className="text-sm font-bold">
                      {formatCurrency(totalDebtService)}
                    </span>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Serviceability Section */}
              <AccordionItem value="serviceability">
                <AccordionTrigger className="text-sm font-semibold">
                  Serviceability
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="field-includeInServiceability"
                        data-testid="field-includeInServiceability"
                        checked={formState.includeInServiceability}
                        onCheckedChange={(checked) =>
                          handleFieldChange('includeInServiceability', !!checked)
                        }
                      />
                      <Label htmlFor="field-includeInServiceability" className="text-sm">
                        Include in serviceability assessment
                      </Label>
                    </div>
                    {formState.includeInServiceability && (
                      <div className="space-y-1">
                        <Label className="text-xs">Contribution Type</Label>
                        <Select
                          value={formState.serviceabilityContributionType}
                          onValueChange={(val) =>
                            handleFieldChange('serviceabilityContributionType', val)
                          }
                        >
                          <SelectTrigger data-testid="field-serviceabilityContributionType">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {SERVICEABILITY_TYPES.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Notes Section */}
              <AccordionItem value="notes">
                <AccordionTrigger className="text-sm font-semibold">
                  Notes
                </AccordionTrigger>
                <AccordionContent>
                  <Textarea
                    data-testid="field-notes"
                    value={formState.notes}
                    onChange={(e) => handleFieldChange('notes', e.target.value)}
                    placeholder="Additional notes about this statement..."
                    rows={3}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseForm}>
              Cancel
            </Button>
            <Button
              data-testid="personal-statement-save-button"
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              )}
              {editingStatement ? 'Update' : 'Save'} Statement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Personal Financial Statement</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteItem?.statementLabel || 'this statement'}&quot;?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-delete"
              onClick={() => deleteItem && deleteMutation.mutate(deleteItem.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
